import { BaseResponse } from '@yleisradio/areena-types';
import {
  AuthError,
  ExpiredSessionError,
  NotFoundError,
  ServerError,
  SilentError,
  UnknownError,
} from '../client/error/errors';
import { AuthFetch } from '../client/domain/AuthFetch';

/**
 * @deprecated use areenaApiFetcher instead
 * @throws {ExpiredSessionError}
 * @throws {AuthError}
 * @throws {NotFoundError}
 * @throws {ServerError}
 * @throws {UnknownError}
 * @throws {Error}
 */
export async function httpFetch<ResponseType extends BaseResponse>(config: {
  fetch: AuthFetch;
  url: string;
  requestInit?: RequestInit;
  isAuthenticated?: boolean;
  silentlyConsumedStatusCodes?: number[];
}): Promise<ResponseType> {
  const {
    fetch,
    url,
    requestInit,
    isAuthenticated = false,
    silentlyConsumedStatusCodes,
  } = config;
  const response = await fetch(url, requestInit).catch(error => {
    console.error(`Failed to fetch ${url} ${error}`);
    throw error;
  });

  const silentError = !!silentlyConsumedStatusCodes?.includes(response.status);

  let errorMessage: string = '';

  if (response.status === 401) {
    if (isAuthenticated) {
      console.error(`Expired session ${url} ${response.status}`);
      errorMessage = 'Expired session';
      if (!silentError) throw new ExpiredSessionError(errorMessage);
    } else {
      console.error(`Authentication failure ${url} ${response.status}`);
      errorMessage = 'Authentication failure';
      if (!silentError) throw new AuthError(errorMessage);
    }
  }
  if (response.status === 404) {
    console.error(`Not found ${url} ${response.status}`);
    errorMessage = 'Not found';
    if (!silentError) throw new NotFoundError(errorMessage);
  }

  if (response.status === 400) {
    const data = (await response.json()) as ResponseType;
    const notifications = data.notifications
      ?.filter(notifications => !!notifications.developerMessage)
      .reduce(
        (acc, current) => acc.concat(`${current?.developerMessage} `),
        ''
      );
    console.error(
      `Failed to fetch ${url}, developerMessages: ${notifications}`
    );
    errorMessage = 'Expired session';
    if (!silentError) throw new ExpiredSessionError(errorMessage);
  }

  if (response.status >= 500) {
    errorMessage = `Internal Server error: ${response.status} occurred while fetching: ${url}`;
    console.error(errorMessage);
    if (!silentError) throw new ServerError(errorMessage);
  }

  if (silentError) {
    throw new SilentError(
      `Consumed following error silently: \n"${errorMessage}"`
    );
  }

  if (response.status !== 200) {
    console.error(`Failed to fetch ${url} ${response.status}`);
    if (!silentError)
      throw new UnknownError(`Failed to fetch ${url} ${response.status}`);
  }

  const responseData = (await response.json()) as ResponseType;

  if (responseData?.notifications) {
    responseData.notifications
      .filter(notification => !!notification.developerMessage)
      .forEach(notification => {
        console.info(
          `API responded with a notification: ${notification.developerMessage}`
        );
      });
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}`);
  }

  return responseData;
}
